/**
 * Utility functions for splitting long text into multiple lines for display
 */

/**
 * Adds a line break delimiter (||) to text that exceeds 35 characters.
 * Attempts to split at the most logical point while keeping lines balanced.
 *
 * @param text - The text to potentially split
 * @returns The text with || delimiter added if needed
 */
export function addLineBreakDelimiter(text: string): string {
  // Don't split if text is 40 chars or less
  if (text.length <= 40) {
    return text;
  }

  // Check if delimiter already exists (avoid double-processing)
  if (text.includes('||')) {
    return text;
  }

  // Try to split at comma first (for multiple artist names)
  const commaIndex = findBestCommaSplit(text);
  if (commaIndex !== -1) {
    const part1 = text.substring(0, commaIndex + 1); // Include the comma
    const part2 = text.substring(commaIndex + 1).trim(); // Trim leading space
    return `${part1}||${part2}`;
  }

  // Otherwise, split at word boundary closest to middle
  const spaceIndex = findBestWordBoundarySplit(text);
  if (spaceIndex !== -1) {
    const part1 = text.substring(0, spaceIndex);
    const part2 = text.substring(spaceIndex + 1); // Skip the space
    return `${part1}||${part2}`;
  }

  // If no good split point found, return as-is
  return text;
}

/**
 * Finds the best comma position to split at, preferring commas closer to the middle
 */
function findBestCommaSplit(text: string): number {
  const commas: number[] = [];
  for (let i = 0; i < text.length; i++) {
    if (text[i] === ',') {
      commas.push(i);
    }
  }

  if (commas.length === 0) {
    return -1;
  }

  // Find comma closest to middle
  const middle = text.length / 2;
  let bestComma = commas[0];
  let bestDistance = Math.abs(commas[0] - middle);

  for (const comma of commas) {
    const distance = Math.abs(comma - middle);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestComma = comma;
    }
  }

  return bestComma;
}

/**
 * Finds the best word boundary (space) to split at, aiming for balanced lines
 * with the second line being longer if uneven
 */
function findBestWordBoundarySplit(text: string): number {
  const spaces: number[] = [];
  for (let i = 0; i < text.length; i++) {
    if (text[i] === ' ') {
      spaces.push(i);
    }
  }

  if (spaces.length === 0) {
    return -1;
  }

  // Find space closest to middle, but slightly prefer earlier spaces
  // to make the second line longer if uneven
  const targetPosition = text.length * 0.45; // Slightly before middle
  let bestSpace = spaces[0];
  let bestDistance = Math.abs(spaces[0] - targetPosition);

  for (const space of spaces) {
    const distance = Math.abs(space - targetPosition);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestSpace = space;
    }
  }

  // Avoid splitting common name patterns
  const beforeSpace = text.substring(Math.max(0, bestSpace - 10), bestSpace);
  const afterSpace = text.substring(bestSpace + 1, Math.min(text.length, bestSpace + 10));

  // Don't split between common name connectors
  if (beforeSpace.match(/\b(feat|ft|with|&|x)\s*$/i)) {
    // Try to find next best space
    const filteredSpaces = spaces.filter(s => s !== bestSpace);
    if (filteredSpaces.length > 0) {
      bestSpace = filteredSpaces.reduce((best, space) => {
        const dist = Math.abs(space - targetPosition);
        const bestDist = Math.abs(best - targetPosition);
        return dist < bestDist ? space : best;
      }, filteredSpaces[0]);
    }
  }

  return bestSpace;
}

/**
 * Splits text at the || delimiter or newline into separate parts
 * Used when building JSON payload for JSON2Video
 *
 * @param text - Text potentially containing || delimiter or newline
 * @returns Object with line1 and optional line2
 */
export function splitAtDelimiter(text: string): { line1: string; line2?: string } {
  if (!text) {
    return { line1: text };
  }

  // Check for || delimiter first (preferred format)
  if (text.includes('||')) {
    const parts = text.split('||');
    return {
      line1: parts[0].trim(),
      line2: parts[1]?.trim()
    };
  }

  // Also check for actual newline characters (may come from direct Google Sheets edits)
  if (text.includes('\n')) {
    const parts = text.split('\n');
    return {
      line1: parts[0].trim(),
      line2: parts[1]?.trim()
    };
  }

  return { line1: text };
}

/**
 * Removes || delimiter for display purposes
 *
 * @param text - Text potentially containing || delimiter
 * @returns Text with delimiter removed
 */
export function removeDelimiter(text: string): string {
  return text.replace(/\|\|/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Strips delimiter before matching/normalization operations
 * This ensures delimiters don't break matching with Master Video List
 *
 * @param text - Text that might contain || delimiter
 * @returns Text without delimiter for matching purposes
 */
export function stripDelimiterForMatching(text: string): string {
  if (!text) return text;
  // Remove || and clean up spacing
  return text.split('||')[0].trim();
}

/**
 * Converts || delimiter to newline for editing in multiline text fields
 * Users see natural line breaks instead of ||
 *
 * @param text - Text potentially containing || delimiter
 * @returns Text with || replaced by newlines
 */
export function delimiterToNewline(text: string): string {
  return text ? text.replace(/\|\|/g, '\n') : '';
}

/**
 * Converts newlines to || delimiter for saving to Google Sheets
 * Preserves the storage format while allowing natural editing
 *
 * @param text - Text potentially containing newlines
 * @returns Text with newlines replaced by ||
 */
export function newlineToDelimiter(text: string): string {
  return text ? text.replace(/\n/g, '||') : '';
}